/**
 * M3 progression HTTP seam: Calling, skills, crafting, contracts, factions, Marks, wardrobe
 * and weapon mods.
 *
 * The server for these endpoints is written concurrently, so every read goes through
 * `apiTry` (404/absent → `null` → the tab shows an empty state) and every response is
 * normalised here rather than in the components. Writes throw, because the player pressed
 * a button and is owed a reason.
 */

import { ARMOR_SLOTS, type ArmorSlot, type FactionId } from '@stillsun/shared';
import { api, apiTry } from './api.js';

// ------------------------------------------------------------------ progress

export interface MarkRecord {
  id: string;
  tier: number;
  unlockedAt?: number;
}

export interface ProgressResponse {
  xp: number;
  level: number;
  skillPoints: number;
  calling: number;
  unlockedNodes: string[];
  counters: Record<string, number>;
  factions: Record<FactionId, number>;
  marks: MarkRecord[];
  titles: string[];
  activeTitle?: string;
  lanternColor?: number;
  /** The player's chosen body (`CharacterId`). Cosmetic, so an absent column is not an error. */
  body?: string;
  /**
   * `Race`, or absent on a character made before the Waking screen asked for one. Absent is not
   * "no body" — the room reads it as Human, which is what those characters have always been.
   */
  race?: number;
}

const num = (v: unknown, fallback = 0): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback;

const strArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];

function numberMap(v: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (v !== null && typeof v === 'object') {
    for (const [k, raw] of Object.entries(v as Record<string, unknown>)) {
      if (typeof raw === 'number' && Number.isFinite(raw)) out[k] = raw;
    }
  }
  return out;
}

function marksOf(v: unknown): MarkRecord[] {
  if (!Array.isArray(v)) return [];
  const out: MarkRecord[] = [];
  for (const raw of v) {
    if (raw === null || typeof raw !== 'object') continue;
    const rec = raw as Record<string, unknown>;
    if (typeof rec.id !== 'string') continue;
    const unlockedAt = rec.unlockedAt;
    out.push({
      id: rec.id,
      tier: num(rec.tier),
      ...(typeof unlockedAt === 'number' ? { unlockedAt } : {}),
    });
  }
  return out;
}

/** Never throws. `null` means the ledger is unreachable — not "you have nothing". */
export async function fetchProgress(token: string): Promise<ProgressResponse | null> {
  const raw = await apiTry<Record<string, unknown>>('/api/progress', { token });
  if (raw === null) return null;
  const factions = numberMap(raw.factions);
  const activeTitle = raw.activeTitle;
  const lanternColor = raw.lanternColor;
  const body = raw.body;
  const race = raw.race;
  return {
    xp: num(raw.xp),
    level: num(raw.level, 1),
    skillPoints: num(raw.skillPoints),
    calling: num(raw.calling),
    unlockedNodes: strArray(raw.unlockedNodes),
    counters: numberMap(raw.counters),
    factions: {
      compact: num(factions.compact),
      hymnal: num(factions.hymnal),
      ferry: num(factions.ferry),
    },
    marks: marksOf(raw.marks),
    titles: strArray(raw.titles),
    ...(typeof activeTitle === 'string' ? { activeTitle } : {}),
    ...(typeof lanternColor === 'number' ? { lanternColor } : {}),
    ...(typeof body === 'string' ? { body } : {}),
    ...(typeof race === 'number' ? { race } : {}),
  };
}

export async function postCalling(token: string, calling: number): Promise<void> {
  await api('/api/calling', { body: { calling }, token });
}

/** Everything the Waking screen chose, and everything the server chose back. */
export interface CharacterCreated {
  /**
   * A **fresh** JWT. The name is baked into the token (the room reads it from there, not from
   * the database), so renaming an account mints a new one and the client must replace the one
   * it is holding or it walks into Lanternfall under its old name.
   */
  token: string;
  name: string;
  /** The race that is *actually stored* — a race is written once and never rewritten. */
  race: number;
  calling: number;
}

/**
 * Character creation: name, race and Calling in one write.
 *
 * One request rather than three, because it is one decision. Three separate writes gave a new
 * player's first minute three ways to half-succeed — a named character with no body, a body with
 * no Calling — and left this screen having to work out which of them had landed.
 *
 * Throws on failure, unlike the wardrobe writes below: the player pressed the only button on the
 * screen and is owed a reason.
 */
export async function postCharacter(
  token: string,
  choice: { name: string; race: number; calling: number },
): Promise<CharacterCreated> {
  const raw = await api<Record<string, unknown>>('/api/character', { body: choice, token });
  return {
    token: typeof raw.token === 'string' ? raw.token : token,
    name: typeof raw.name === 'string' ? raw.name : choice.name,
    race: num(raw.race, choice.race),
    calling: num(raw.calling, choice.calling),
  };
}

export async function unlockNode(token: string, nodeId: string): Promise<void> {
  await api('/api/skills/unlock', { body: { nodeId }, token });
}

export async function respecSkills(token: string): Promise<void> {
  await api('/api/skills/respec', { body: {}, token });
}

// ------------------------------------------------------------------ crafting

export interface CraftResponse {
  /** Recipe ids the server is offering. Shape mirrors `shared.Recipe`; only the id is
   * load-bearing here — everything drawn comes from `RECIPES` so the copy stays in sync. */
  recipeIds: string[];
  workbenchLevel: number;
}

export async function fetchCraft(token: string): Promise<CraftResponse | null> {
  const raw = await apiTry<Record<string, unknown>>('/api/craft', { token });
  if (raw === null) return null;
  const list = Array.isArray(raw.recipes) ? raw.recipes : [];
  const ids: string[] = [];
  for (const r of list) {
    if (typeof r === 'string') ids.push(r);
    else if (r !== null && typeof r === 'object') {
      const id = (r as Record<string, unknown>).id;
      if (typeof id === 'string') ids.push(id);
    }
  }
  return { recipeIds: ids, workbenchLevel: num(raw.workbenchLevel, 1) };
}

// ----------------------------------------------------------------- contracts

export interface ActiveContract {
  id: string;
  title: string;
  progress: number;
  goal: number;
  reward: number;
  faction: string;
  cadence: string;
  claimable: boolean;
  claimed: boolean;
}

export interface ContractsResponse {
  active: ActiveContract[];
  resetsAt: number;
}

/** Rewards have arrived as both a bare Wick number and the shared `{wick, rep, xp}` object. */
function rewardWick(v: unknown): number {
  if (typeof v === 'number') return v;
  if (v !== null && typeof v === 'object') return num((v as Record<string, unknown>).wick);
  return 0;
}

export async function fetchContracts(token: string): Promise<ContractsResponse | null> {
  const raw = await apiTry<Record<string, unknown>>('/api/contracts', { token });
  if (raw === null) return null;
  // The board has shipped as a bare array and as `{active}`; accept either.
  const list = Array.isArray(raw) ? raw : Array.isArray(raw.active) ? raw.active : [];
  const active: ActiveContract[] = [];
  for (const item of list) {
    if (item === null || typeof item !== 'object') continue;
    const c = item as Record<string, unknown>;
    const id = typeof c.id === 'string' ? c.id : typeof c.id === 'number' ? String(c.id) : '';
    if (id.length === 0) continue;
    active.push({
      id,
      title: typeof c.title === 'string' ? c.title : id,
      progress: num(c.progress),
      goal: Math.max(1, num(c.goal, 1)),
      reward: rewardWick(c.reward),
      faction: typeof c.faction === 'string' ? c.faction : 'compact',
      cadence: typeof c.cadence === 'string' ? c.cadence : 'daily',
      claimable: c.claimable === true,
      claimed: c.claimed === true,
    });
  }
  return { active, resetsAt: num(raw.resetsAt) };
}

export async function claimContract(token: string, id: string): Promise<void> {
  await api(`/api/contracts/${encodeURIComponent(id)}/claim`, { body: {}, token });
}

// --------------------------------------------------------------------- marks

/** `/api/marks` is a convenience mirror of `progress.marks`; either source works. */
export async function fetchMarks(token: string): Promise<MarkRecord[] | null> {
  const raw = await apiTry<unknown>('/api/marks', { token });
  if (raw === null) return null;
  if (Array.isArray(raw)) return marksOf(raw);
  if (typeof raw === 'object') return marksOf((raw as Record<string, unknown>).marks);
  return [];
}

// ------------------------------------------------------------------ wardrobe

export interface WardrobeChange {
  title?: string;
  lanternColor?: number;
  /**
   * Which of the four playable bodies the player wears (`CharacterId`). The Waking screen
   * writes this before the tutorial; the column and the route landed after the rest of the
   * wardrobe, so a caller must treat a 400 or a 404 here as "not yet" and carry on — a body
   * is cosmetic and is never worth stopping a player at the door for.
   */
  body?: string;
}

/** `exactOptionalPropertyTypes`: build the body by spreading, never `{title: undefined}`. */
export async function postWardrobe(token: string, change: WardrobeChange): Promise<void> {
  const body: Record<string, unknown> = {};
  if (change.title !== undefined) body.title = change.title;
  if (change.lanternColor !== undefined) body.lanternColor = change.lanternColor;
  if (change.body !== undefined) body.body = change.body;
  await api('/api/wardrobe', { body, token });
}

// ----------------------------------------------------------------- equipment

/**
 * The seven paperdoll slots: six armour and the weapon.
 *
 * Phase 2c added `shoulders` (the third visible piece) and folded the weapon in — the server's
 * `game/equipment.ts` addresses it as the slot name `'weapon'`, backed by `ItemLocation.Primary`,
 * so the doll has one write path rather than one for armour and a stash move for the stick.
 *
 * Normalised into a total record so the paperdoll can index it without `noUncheckedIndexedAccess`
 * handing back `| undefined` at every slot — an unequipped slot is `null`, which is a state the
 * doll draws, not an absence.
 */
export type EquipSlot = ArmorSlot | 'weapon';
export const EQUIP_SLOTS: readonly EquipSlot[] = [...ARMOR_SLOTS, 'weapon'];

export type EquippedPiece = { uid: number; itemId: string } | null;
export interface EquipmentResponse {
  equipped: Record<EquipSlot, EquippedPiece>;
  /** The packed rarity word remote rigs tint themselves from (`packCosmetics`). */
  cosmetics: number;
}

export const EMPTY_EQUIPMENT: Record<EquipSlot, EquippedPiece> = {
  head: null,
  shoulders: null,
  chest: null,
  hands: null,
  legs: null,
  feet: null,
  weapon: null,
};

function equipmentOf(raw: Record<string, unknown> | null): EquipmentResponse | null {
  if (raw === null) return null;
  const src = raw.equipped;
  const rec = src !== null && typeof src === 'object' ? (src as Record<string, unknown>) : {};
  const equipped: Record<EquipSlot, EquippedPiece> = { ...EMPTY_EQUIPMENT };
  for (const slot of EQUIP_SLOTS) {
    const entry = rec[slot];
    if (entry === null || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    if (typeof e.uid === 'number' && typeof e.itemId === 'string') {
      equipped[slot] = { uid: e.uid, itemId: e.itemId };
    }
  }
  return { equipped, cosmetics: num(raw.cosmetics) };
}

/** Never throws. `null` means the ledger is unreachable — not "you are wearing nothing". */
export async function getEquipment(token: string): Promise<EquipmentResponse | null> {
  return equipmentOf(await apiTry<Record<string, unknown>>('/api/equipment', { token }));
}

/**
 * Equip `uid` into `slot`, or unequip it with `uid: null`. Throws on rejection — the player
 * pressed something and is owed a reason (`not_found` / `not_armor` / `not_a_weapon` /
 * `wrong_slot` / `wrong_calling` / `level_too_low` / `no_room`, translated to English by the
 * caller).
 */
export async function putEquipment(
  token: string,
  slot: EquipSlot,
  uid: number | null,
): Promise<EquipmentResponse> {
  const raw = await api<Record<string, unknown>>('/api/equipment', {
    method: 'PUT',
    body: { slot, uid },
    token,
  });
  return equipmentOf(raw) ?? { equipped: { ...EMPTY_EQUIPMENT }, cosmetics: 0 };
}
